import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { MY_CGPA, SAVE_COURSES } from '../graphql';
import { Plus, Trash2, Save, GraduationCap } from 'lucide-react';
import { RadialBarChart, RadialBar, ResponsiveContainer, Tooltip } from 'recharts';

const GRADES = ['O', 'A+', 'A', 'B+', 'B', 'C', 'F'];
const GRADE_POINTS = { O: 10, 'A+': 9, A: 8, 'B+': 7, B: 6, C: 5, F: 0 };

export default function CgpaPage() {
  const { data, loading } = useQuery(MY_CGPA);
  const [saveCourses] = useMutation(SAVE_COURSES, { refetchQueries: [MY_CGPA] });
  const [editing, setEditing] = useState(false);
  const [courses, setCourses] = useState([]);

  const startEdit = () => {
    const all = data?.myCgpa?.semesters?.flatMap(s => s.courses.map(c => ({
      ...c, semester: s.semester
    }))) || [];
    setCourses(all.length ? all : [{ name: '', credits: 3, grade: 'A', semester: 1 }]);
    setEditing(true);
  };

  const addCourse = () => setCourses([...courses, { name: '', credits: 3, grade: 'A', semester: 1 }]);
  const removeCourse = (i) => setCourses(courses.filter((_, idx) => idx !== i));
  const updateCourse = (i, k, v) => setCourses(courses.map((c, idx) => idx === i ? { ...c, [k]: v } : c));

  const handleSave = async () => {
    await saveCourses({ variables: { courses: courses.map(c => ({
      name: c.name, credits: parseInt(c.credits), grade: c.grade, semester: parseInt(c.semester)
    })) }});
    setEditing(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-brand-600 border-t-transparent rounded-full" /></div>;

  const cgpa = data?.myCgpa?.cgpa || 0;
  const semesters = data?.myCgpa?.semesters || [];

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">CGPA Calculator</h1>
        <button className="btn-primary flex items-center gap-2" onClick={editing ? handleSave : startEdit}>
          {editing ? <><Save size={18} /> Save</> : <><Plus size={18} /> {semesters.length ? 'Edit Courses' : 'Add Courses'}</>}
        </button>
      </div>

      {/* CGPA display */}
      {!editing && cgpa > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="card flex items-center gap-6">
            <div className="w-32 h-32">
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart cx="50%" cy="50%" innerRadius="60%" outerRadius="100%"
                  data={[{ value: (cgpa / 10) * 100, fill: '#1a73e8' }]}>
                  <RadialBar dataKey="value" cornerRadius={10} />
                </RadialBarChart>
              </ResponsiveContainer>
            </div>
            <div>
              <p className="text-sm text-gray-500">Cumulative GPA</p>
              <p className="text-5xl font-bold text-brand-600">{cgpa.toFixed(2)}</p>
              <p className="text-sm text-gray-500 mt-1">out of 10.00</p>
            </div>
          </div>

          <div className="card">
            <h3 className="font-semibold mb-3">Semester-wise SGPA</h3>
            <div className="space-y-2">
              {semesters.map(sem => (
                <div key={sem.semester} className="flex items-center gap-3">
                  <span className="text-sm text-gray-500 w-20">Sem {sem.semester}</span>
                  <div className="flex-1 h-2 bg-gray-100 rounded-full">
                    <div className="h-2 bg-brand-500 rounded-full" style={{ width: `${(sem.sgpa / 10) * 100}%` }} />
                  </div>
                  <span className="text-sm font-semibold text-gray-700 w-12 text-right">{sem.sgpa.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Course editor */}
      {editing && (
        <div className="card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="pb-3 pr-3">Course</th>
                  <th className="pb-3 pr-3 w-24">Credits</th>
                  <th className="pb-3 pr-3 w-24">Grade</th>
                  <th className="pb-3 pr-3 w-20">Points</th>
                  <th className="pb-3 pr-3 w-20">Sem</th>
                  <th className="pb-3 w-10"></th>
                </tr>
              </thead>
              <tbody className="space-y-2">
                {courses.map((c, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="py-2 pr-3">
                      <input className="input !py-1.5 text-sm" value={c.name}
                        onChange={e => updateCourse(i, 'name', e.target.value)} placeholder="Course name" />
                    </td>
                    <td className="py-2 pr-3">
                      <input className="input !py-1.5 text-sm w-full" type="number" min="1" max="6"
                        value={c.credits} onChange={e => updateCourse(i, 'credits', e.target.value)} />
                    </td>
                    <td className="py-2 pr-3">
                      <select className="input !py-1.5 text-sm" value={c.grade}
                        onChange={e => updateCourse(i, 'grade', e.target.value)}>
                        {GRADES.map(g => <option key={g}>{g}</option>)}
                      </select>
                    </td>
                    <td className="py-2 pr-3 font-semibold text-brand-600">
                      {GRADE_POINTS[c.grade] || 0}
                    </td>
                    <td className="py-2 pr-3">
                      <input className="input !py-1.5 text-sm w-full" type="number" min="1" max="8"
                        value={c.semester} onChange={e => updateCourse(i, 'semester', e.target.value)} />
                    </td>
                    <td className="py-2">
                      <button onClick={() => removeCourse(i)} className="p-1 text-gray-400 hover:text-red-500">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex gap-3 mt-4">
            <button className="btn-secondary flex items-center gap-2" onClick={addCourse}>
              <Plus size={16} /> Add Course
            </button>
            <button className="btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}

      {!editing && cgpa === 0 && (
        <div className="text-center py-16 text-gray-400">
          <GraduationCap size={48} className="mx-auto mb-4 text-gray-300" />
          <p className="font-medium">No courses yet.</p>
          <p className="text-sm">Add your courses to calculate CGPA.</p>
        </div>
      )}
    </div>
  );
}
